/**
 * Clubs API helpers.
 */

import { compressImageForUpload } from './mediaCompress';
import { uploadFormData } from './uploadHelpers';

export async function getClubs(myOnly = false) {
  const url = myOnly ? '/api/clubs?my=true' : '/api/clubs';
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch clubs');
  return res.json();
}

export async function getClub(clubId) {
  const res = await fetch(`/api/clubs/${clubId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch club');
  return res.json();
}

export async function getClubMembers(clubId) {
  const res = await fetch(`/api/clubs/${clubId}/members`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch members');
  return res.json();
}

export async function createClub(data) {
  const res = await fetch('/api/clubs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create club');
  }
  return res.json();
}

export async function updateClub(clubId, data) {
  const res = await fetch(`/api/clubs/${clubId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update club');
  }
  return res.json();
}

/** Upload image and set club avatar_url (owner/admin). */
export async function uploadClubAvatar(clubId, file, options = {}) {
  const compressed = await compressImageForUpload(file);
  const formData = new FormData();
  formData.append('file', compressed);
  formData.append('club_id', String(clubId));
  try {
    return await uploadFormData('/api/media/club-avatar', formData, {
      onProgress: options.onProgress,
    });
  } catch (err) {
    throw new Error(err.message || 'Failed to upload club picture');
  }
}

export async function deleteClub(clubId) {
  const res = await fetch(`/api/clubs/${clubId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete club');
  }
  return res.json();
}

export async function joinClub(clubId) {
  const res = await fetch(`/api/clubs/${clubId}/join`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to join club');
  }
  return res.json();
}

export async function leaveClub(clubId) {
  const res = await fetch(`/api/clubs/${clubId}/leave`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to leave club');
  }
  return res.json();
}

export async function inviteToClub(clubId, userId) {
  const res = await fetch(`/api/clubs/${clubId}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to invite');
  }
  return res.json();
}

export async function getClubJoinRequests(clubId) {
  const res = await fetch(`/api/clubs/${clubId}/join-requests`, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch join requests');
  }
  return res.json();
}

export async function acceptClubJoinRequest(clubId, requesterUserId) {
  const res = await fetch(`/api/clubs/${clubId}/join-requests/${requesterUserId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to accept request');
  }
  return res.json();
}

export async function declineClubJoinRequest(clubId, requesterUserId) {
  const res = await fetch(`/api/clubs/${clubId}/join-requests/${requesterUserId}/decline`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to decline request');
  }
  return res.json();
}
